import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { OrganizationSettingsStore } from './organization-settings.store';
import {
    OrganizationSettingsObject,
} from '@libs/types/src/organization/object-type/organization/organization-settings.object';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { FormArray, FormBuilder, FormsModule } from '@angular/forms';
import {
    createCollectionCategoryForm,
    createDonationFormForm,
    createRaiffeisenAccountToFormForm,
    createSporitelnaAccountToFormForm,
    prepareForm,
    sporitelnaForm,
} from './organization-settings.forms';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ibanToAccountNumber } from '../../../../../../../utils/iban-to-account-number';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import {
    SporitelnaBankAccountObject,
} from '@libs/types/src/chcrm/object-type/sporitelna/sporitelna-bank-account.object';
import { donorCategoryThresholdOptions } from '@libs/types/src/chcrm/enum/donor-category/donor-category-threshold.enum';
import { convertCzechAccountToIban, tryConvertCzechAccountToIban } from '../../../../../../../../../iban/src';
import { FileUpload, FileUploadEvent } from '@openng/optimus-ui/fileupload';
import { HttpClient } from '@angular/common/http';
import { selectCurrentOrganizationId } from '../../../../../../store/global/global.selectors';
import { SaveButtonComponent } from '../../../../../../components/kit/button/save-button.component';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { DeleteButtonComponent } from '../../../../../../components/kit/button/delete-button.component';
import { AsyncPipe, KeyValuePipe, NgTemplateOutlet } from '@angular/common';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';
import { NumberInputComponent } from '../../../../../../components/kit/input/text-input/number-input.component';
import { PasswordInputComponent } from '../../../../../../components/kit/input/password-input/password-input.component';
import { Divider } from '@openng/optimus-ui/divider';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { CheckOrCrossComponent } from '../../../../../../components/kit/check-or-cross/check-or-cross.component';
import { SwitchInputComponent } from '../../../../../../components/kit/input/switch-input/switch-input.component';
import {
    OrganizationSettingsMadoComponent,
} from '../../../../../../components/mado/organization/organization-settings/organization-settings-mado.component';
import {
    OrganizationSettingsFiscalYearComponent,
} from '../../../../../../components/mado/organization/organization-settings-fiscal-year/organization-settings-fiscal-year.component';
import {
    OrganizationSettingsDepartmentComponent,
} from '../../../../../../components/mado/organization/organization-settings-department/organization-settings-department.component';
import {
    OrganizationSettingsProjectComponent,
} from '../../../../../../components/mado/organization/organization-settings-project/organization-settings-project.component';
import {
    OrganizationSettingsBudgetChapterComponent,
} from '../../../../../../components/mado/organization/organization-settings-budget-chapter/organization-settings-budget-chapter.component';
import {
    OrganizationSettingsCurrencyComponent,
} from '../../../../../../components/mado/organization/organization-settings-currency/organization-settings-currency.component';
import {
    OrganizationSettingsJournalComponent,
} from '../../../../../../components/mado/organization/organization-settings-journal/organization-settings-journal.component';
import {
    OrganizationSettingsVatCodeComponent,
} from '../../../../../../components/mado/organization/organization-settings-vat-code/organization-settings-vat-code.component';
import {
    OrganizationSettingsMadoTransactionsComponent,
} from '../../../../../../components/mado/organization/organization-settings/organization-settings-mado-transactions.component';
import {
    OrganizationSettingsMadoCreditAgreementsComponent,
} from '../../../../../../components/mado/organization/organization-settings-mado-credit-agreements/organization-settings-mado-credit-agreements.component';
import { LocalDatePipe } from '@libs/shared/pipes/local-date.pipe';
import {
    SporitelnaBankAccountComponent,
} from '../../../../components/organization/sporitelna-bank-account/sporitelna-bank-account.component';
import { FileGqlService } from '../../../../../../services/file.gql-service';
import { uploadFile } from '../../../../../../utils/files.utils';
import {
    OrganizationSettingsWebGatewayType,
} from '@libs/types/src/organization/types/organization-settings/organization-settings-web-gateway.type';
import {
    OrganizationSettingsWebGatewayComponent,
} from './web-gateway/organization-settings-web-gateway.component';
import {
    MicrosoftGraphKeysSettingComponent,
} from '../../../../../../components/inbox/organization-settings/microsoft-graph-keys-setting/microsoft-graph-keys-setting.component';
import {
    OrganizationTrashSettingsComponent,
} from './organization-trash-settings.component';

const bankRedirectBase = 'https://bezpecnost.csas.cz/api/psd2/fl/oidc/v1/auth';
const bankCallbackUri = 'https://chcrm.i2ginfra.cz/api/v1/crm/handle-bank-login';
const bankClientId = 'b16588cd-44b6-4332-a05e-e69b99573cc8';
const bankResponseType = 'code';
const bankAccessType = 'offline';

@Component({
    selector: 'app-organization-settings',
    templateUrl: 'organization-settings.component.html',
    styleUrls: ['organization-settings.component.scss'],
    providers: [OrganizationSettingsStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SaveButtonComponent,
        ButtonComponent,
        TranslatePipe,
        TextInputComponent,
        SelectInputComponent,
        DeleteButtonComponent,
        NgTemplateOutlet,
        AsyncPipe,
        Tooltip,
        CheckInputComponent,
        NumberInputComponent,
        PasswordInputComponent,
        Divider,
        KeyValuePipe,
        DatePicker,
        FormsModule,
        FileUpload,
        CheckOrCrossComponent,
        SwitchInputComponent,
        OrganizationSettingsMadoComponent,
        OrganizationSettingsFiscalYearComponent,
        OrganizationSettingsDepartmentComponent,
        OrganizationSettingsProjectComponent,
        OrganizationSettingsBudgetChapterComponent,
        OrganizationSettingsCurrencyComponent,
        OrganizationSettingsJournalComponent,
        OrganizationSettingsVatCodeComponent,
        OrganizationSettingsMadoTransactionsComponent,
        OrganizationSettingsMadoCreditAgreementsComponent,
        LocalDatePipe,
        SporitelnaBankAccountComponent,
        OrganizationSettingsWebGatewayComponent,
        MicrosoftGraphKeysSettingComponent,
        OrganizationTrashSettingsComponent,
    ],
})
export class OrganizationSettingsComponent extends SubscriptionComponent implements OnInit {

    protected readonly store = inject(OrganizationSettingsStore);
    private readonly fb = inject(FormBuilder);
    private readonly translate = inject(TranslateService);
    private readonly confirmSvc = inject(ConfirmationService);
    private readonly http = inject(HttpClient);
    private readonly fileGqlService = inject(FileGqlService);

    @Input() settings!: OrganizationSettingsObject | null;
    settingsData!: Record<string, any>;

    protected commonForm = this.fb.group({
        active: this.fb.control(true),
    });
    protected form = this.fb.group({});
    protected webGatewaySettings: OrganizationSettingsWebGatewayType = { integrations: [], tokens: {} };
    protected redirectUrl = '';
    protected date = new Date();

    constructor() {
        super();
        this.store.registerSuccessMessage('updateOrganizationSetting', 'common.updated');
    }

    public async ngOnInit(): Promise<void> {
        //TODO: sporitelna is collossally stupid, and will only allow one redirectUrl afte logging
        // meaning we can't say what org we want to sign in for. WTF. We should do redirect from FE when
        // we receive the code, and then call this endpoint with currently selected org.
        // But we would have to change the redirect url setting on clients side. Fucking hell
        // FIX IT LATER
        const orgId = this.settings!.organizationId;
        const state = encodeURIComponent(JSON.stringify({ orgId }));
        this.redirectUrl = `${bankRedirectBase}?redirect_uri=${bankCallbackUri}&client_id=${bankClientId}&response_type=${bankResponseType}&access_type=${bankAccessType}&state=${state}`;
        const data = JSON.parse(this.settings?.data || '{}');
        this.settingsData = data;
        if (this.settings?.key === OrganizationSettingTypeEnum.WEB_GATEWAY) {
            const webGatewayData = data as Partial<OrganizationSettingsWebGatewayType>;
            this.webGatewaySettings = {
                integrations: webGatewayData.integrations ?? [],
                tokens: webGatewayData.tokens ?? {},
            };
        }
        this.form = prepareForm(this.settings?.key || '', this.fb, data);
        this.form.patchValue(data, { emitEvent: false });
        this.commonForm.patchValue({ active: this.settings?.active || false }, { emitEvent: false });
        this.subs.push(
            this.commonForm.controls.active.valueChanges.subscribe((it) => {
                if (it !== this.settings?.active) {
                    this.store.api.updateOrganizationSetting({
                        key: this.settings!.key,
                        organizationId: this.settings!.organizationId,
                        active: it || false,
                    });
                }
            }),
        );

        if (this.settings?.key === OrganizationSettingTypeEnum.SPORITELNA) {
            this.subs.push(
                this.store.apiSuccess.getBanks.subscribe(
                    (banks: SporitelnaBankAccountObject[]) => {
                        this.form = sporitelnaForm(this.fb, data, banks);
                        this.form.patchValue(
                            JSON.parse(this.settings?.data || '{}'),
                        );
                    },
                ),
            );
            this.store.api.getBanks({
                organizationId: this.settings.organizationId,
            } as any);
        }
    }

    protected saveForm(webGatewayData?: OrganizationSettingsWebGatewayType): void {
        if (isInvalid(this.form)) return;

        this.store.api.updateOrganizationSetting({
            key: this.settings!.key,
            organizationId: this.settings!.organizationId,
            data: JSON.stringify({
                ...this.settingsData,
                ...this.form.value,
                ...webGatewayData,
            }),
        });
        this.form.markAsUntouched();
    }

    protected delete(): void {
        this.store.api.removeOrganizationSetting({
            organizationId: this.settings!.organizationId,
            key: this.settings!.key,
        });
    }

    protected addFormRow(
        fg: FormArray,
        createGroup: (fb: FormBuilder) => {},
    ): void {
        fg.push(createGroup(this.fb));
    }

    protected removeFormRow(fg: FormArray, index: number): void {
        this.form.markAsTouched();
        fg.removeAt(index);
    }

    protected async onCertUpload(param: FileUploadEvent): Promise<void> {
        const uploadFileUrl = await this.fileGqlService.getPresignedUrl({
            name: param.files[0].name,
            categoryId: null,
            categoryCode: 'certs',
            owner: `Raiffeisen_${this.settings!.organizationId}`,
            replace: true,
        } as any);

        await uploadFile({
            url: uploadFileUrl.url,
            file: param.files[0],
        });
        this.form.markAsTouched();

        const result = await this.fileGqlService.confirmFileUpload({ id: uploadFileUrl.id });

        this.form.patchValue({ certFilepath: result.path });
        // this.upload.clear();
    }

    async syncSporitelna(): Promise<void> {
        const organizationId = await this.store.get(
            this.store.globalStore!.select(selectCurrentOrganizationId),
        );
        const date = this.date;
        this.http
            .get(
                `/api/v1/crm/donation/import-sporitelna/${date.getFullYear()}-${
                    date.getMonth() + 1
                }-${date.getDate()}/${organizationId}`,
            )
            .subscribe();
    }

    async syncRaiff(): Promise<void> {
        const organizationId = await this.store.get(
            this.store.globalStore!.select(selectCurrentOrganizationId),
        );
        const date = this.date;
        this.http
            .get(
                `/api/v1/crm/donation/import-rb/${date.getFullYear()}-${
                    date.getMonth() + 1
                }-${date.getDate()}/${organizationId}`,
            )
            .subscribe();
    }

    protected readonly OrganizationSettingTypeEnum =
        OrganizationSettingTypeEnum;
    protected readonly ibanToAccountNumber = ibanToAccountNumber;
    protected readonly donorCategoryThresholdOptions =
        donorCategoryThresholdOptions;
    protected readonly createCollectionCategoryForm =
        createCollectionCategoryForm;
    protected readonly createDonationFormForm = createDonationFormForm;
    protected readonly convertCzechAccountToIban = convertCzechAccountToIban;
    protected readonly tryConvertCzechAccountToIban =
        tryConvertCzechAccountToIban;
    protected readonly createSporitelnaAccountToFormForm =
        createSporitelnaAccountToFormForm;
    protected readonly createRaiffeisenAccountToFormForm =
        createRaiffeisenAccountToFormForm;
}
